import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link, useParams, Redirect } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import SVG from 'react-inlinesvg'
import axios from 'axios'
import { Edit, Tag } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import DisclosureAgreementCheckbox from './DisclosureAgreementCheckbox/DisclosureAgreementCheckbox'
import CheckoutForm from '../../leevo_ui/CheckoutForm/CheckoutForm'
import useAuth from '../../../utilities/hooks/useAuth'
import { priceInDollars } from '../../../utilities/helpers'
import { requestCreator } from '../../../utilities/requests'
import { requiredToTop } from '../../../utilities/helpers.js'

import poweredByStripe from '../../../images/powered-by-stripe.svg'
import sslCheckout from '../../../images/ssl-checkout.png'

const { get: getFacility, cancel: cancelFacilityRequest } = requestCreator()
const { post: finishCheckoutSession, cancel: cancelCheckoutSessionRequest } = requestCreator()
function Checkout({
  students,
  onCompletedCheckout,
  lineItems,
  couponCode = '',
  onCouponCodeChange,
  couponCodeError = '',
  checkoutReady = true,
  couponCodeAmount = null,
  couponCodePromotionAmount = null,
  total,
  demo = false,
  disclosures: initialDisclosures = []
}) {
  const [stripeAccount, setStripeAccount] = useState(null)
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const { isSignedIn } = useAuth()
  const { facility_id } = useParams()
  const [disclosures, setDisclosures] = useState(
    (initialDisclosures || []).sort(requiredToTop).map((d) => ({ ...d, agreed: false }))
  )

  const hasAgreedToAllRequiredDisclosures = useMemo(
    () => disclosures.every((d) => !d.required || d.agreed),
    [disclosures]
  )

  const redirect = !isSignedIn || !lineItems || lineItems.length === 0 || !students || students.length === 0

  function handleCheckoutSubmit(paymentMethod) {
    if (demo) return Promise.reject(new Error('Registration completion is disabled in the shared demo.'))
    if (!checkoutReady || isCouponSubmitting || !hasAgreedToAllRequiredDisclosures) {
      return Promise.reject(new Error('Please finish reviewing your checkout before continuing.'))
    }
    setIsPaymentSubmitting(true)
    setPaymentError('')
    const url = `/api/facilities/${facility_id}/checkouts`
    return finishCheckoutSession(url, {
      sourceId: paymentMethod?.id,
      students,
      couponCode,
      disclosures: disclosures.map(({ id, agreed }) => ({ id, agreed }))
    })
      .then((response) => {
        return onCompletedCheckout(response)
      })
      .catch((error) => {
        setPaymentError(error.response?.data?.error || 'Unable to complete registration. Please try again.')
        throw error
      })
      .finally(() => setIsPaymentSubmitting(false))
  }

  useEffect(() => {
    getFacility(`/api/facilities/${facility_id}`)
      .then(({ stripeUserId }) => {
        setStripeAccount(stripeUserId)
      })
      .catch((error) => !axios.isCancel(error) && history.push('/500'))

    return () => {
      cancelFacilityRequest()
      cancelCheckoutSessionRequest()
    }
  }, [facility_id])

  const {
    handleSubmit: handleCouponSubmit,
    register: registerCoupon,
    formState: { isSubmitting: isCouponSubmitting }
  } = useForm({ defaultValues: { couponCode } })
  const [showCouponForm, setShowCouponForm] = useState(false)

  async function handleCouponFormSubmit(data) {
    await onCouponCodeChange({ couponCode: data.couponCode })
  }

  return redirect ? (
    <Redirect to={isSignedIn ? `/facilities/${facility_id}/register/classes` : `/facilities/${facility_id}/register`} />
  ) : (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Card className="mb-6">
        <CardHeader>
          <div className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Checkout</CardTitle>
              <CardDescription>
                {demo ? 'Review sample prices, discounts, and disclosures' : 'Review your order and complete payment'}
              </CardDescription>
            </div>
            <Link to={`/facilities/${facility_id}/register`}>
              <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Order Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {lineItems.map(({ id, description, price }) => (
              <div
                key={id}
                className="flex justify-between items-start py-3 border-b border-gray-200 last:border-0"
                data-cy={`line-item-${id}`}
              >
                <span className="text-sm font-medium text-gray-700" data-cy="line-item-description">
                  {description}
                </span>
                <span className="text-sm font-semibold text-gray-900 ml-4" data-cy="line-item-price">
                  ${priceInDollars(price)}
                </span>
              </div>
            ))}

            {couponCodeAmount && (
              <div className="flex justify-between items-start py-3 border-b border-gray-200 text-green-700">
                <span className="text-sm font-medium">Coupon Code for {couponCodePromotionAmount} off</span>
                <span className="text-sm font-semibold ml-4" data-cy="coupon-code-amount">
                  -${priceInDollars(couponCodeAmount)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center pt-4" data-cy="total">
              <span className="text-lg font-bold text-gray-900">Total</span>
              <span className="text-lg font-bold text-gray-900">${priceInDollars(total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
      {disclosures.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Legal Agreements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {disclosures.map(({ id, name, agreement, disclosureType, required, agreed }) => (
                <DisclosureAgreementCheckbox
                  key={id}
                  id={id}
                  disclosureType={disclosureType}
                  disclosureName={name}
                  agreement={agreement}
                  required={required}
                  onClick={() =>
                    setDisclosures((disclosures) =>
                      disclosures.map((d) => (d.id === id ? { ...d, agreed: !d.agreed } : d))
                    )
                  }
                  value={agreed}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{demo ? 'Registration Preview' : 'Payment Information'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!demo && !hasAgreedToAllRequiredDisclosures && (
            <div className="p-4 rounded-md bg-purple-50 border border-purple-200">
              <p className="text-sm text-purple-800">👆 You must agree to required agreements before proceeding.</p>
            </div>
          )}

          <div className="space-y-3">
            {!showCouponForm ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCouponForm(true)}
                className="w-full md:w-auto"
              >
                <Tag className="w-4 h-4 mr-2" />
                Have a coupon code?
              </Button>
            ) : (
              <form onSubmit={handleCouponSubmit(handleCouponFormSubmit)} className="space-y-2">
                <Label htmlFor="couponCode">Coupon Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="couponCode"
                    placeholder="Enter code"
                    {...registerCoupon('couponCode', { required: 'Please enter a coupon code' })}
                    disabled={isCouponSubmitting || isPaymentSubmitting}
                  />
                  <Button
                    type="submit"
                    disabled={isCouponSubmitting || isPaymentSubmitting}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {isCouponSubmitting ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
                {(couponCode || couponCodeError) && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={isCouponSubmitting || isPaymentSubmitting}
                    onClick={() => onCouponCodeChange({ couponCode: '' })}
                  >
                    Remove coupon
                  </Button>
                )}
                {couponCodeError && <p className="text-sm text-red-600">{couponCodeError}</p>}
              </form>
            )}
          </div>

          {paymentError && (
            <p role="alert" className="text-sm text-red-600">
              {paymentError}
            </p>
          )}
          {demo ? (
            <p role="note" className="rounded-md border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900">
              You have reached the end of the demo registration preview. You can review prices, apply coupons, and
              explore disclosures. Enrollment and payment will not be submitted.
            </p>
          ) : total === 0 ? (
            <Button
              disabled={
                !hasAgreedToAllRequiredDisclosures || !checkoutReady || isCouponSubmitting || isPaymentSubmitting
              }
              onClick={() => handleCheckoutSubmit().catch(() => {})}
            >
              {isPaymentSubmitting ? 'Completing registration...' : 'Complete registration'}
            </Button>
          ) : (
            stripeAccount && (
              <div className="pt-4">
                <CheckoutForm
                  disabled={
                    !hasAgreedToAllRequiredDisclosures || !checkoutReady || isCouponSubmitting || isPaymentSubmitting
                  }
                  submitButtonLabel={`Pay $${priceInDollars(total)}`}
                  onSubmit={handleCheckoutSubmit}
                />
              </div>
            )
          )}
        </CardContent>
      </Card>

      {!demo && (
        <div className="flex items-center justify-between gap-4 mt-6">
          <SVG src={poweredByStripe} className="w-32" />
          <img alt="Fully secured SSL checkout" src={sslCheckout} className="w-32" />
        </div>
      )}
    </div>
  )
}

Checkout.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      selectedKlasses: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
        })
      )
    })
  ).isRequired,
  onCompletedCheckout: PropTypes.func.isRequired,
  lineItems: PropTypes.array.isRequired,
  couponCodeAmount: PropTypes.number,
  couponCodePromotionAmount: PropTypes.string,
  total: PropTypes.number.isRequired,
  demo: PropTypes.bool,
  onCouponCodeChange: PropTypes.func.isRequired,
  couponCodeError: PropTypes.string,
  checkoutReady: PropTypes.bool,
  couponCode: PropTypes.string,
  disclosures: PropTypes.array
}

export default Checkout
